import { queryOptions } from '@tanstack/react-query';
import type { CapacityMode, FillMode, ProjectionResult } from '@rundown/domain';
import type { BoardColumn, Priority, SprintState, WorkType } from '@rundown/types';
import { api } from '../../lib/api';

/**
 * Backlog transport.
 *
 * ── ASSUMED CONTRACT ────────────────────────────────────────────────────────
 *   GET   /workspaces/:workspaceId/backlog              -> BacklogRow[]
 *   PATCH /workspaces/:workspaceId/backlog/:id/rank      { beforeId?, afterId? } -> BacklogRow
 *   GET   /workspaces/:workspaceId/backlog/projection    ?capacityMode&fill -> ProjectionResponse
 *
 * Query keys for this feature are local (`backlogKeys` below) rather than
 * added to lib/queryKeys.ts, which another agent owns concurrently — see the
 * file's own HARD RULE comment for why every key still starts
 * `['ws', workspaceId, …]` regardless of which file declares it.
 */

/** The client/internal split, same reasoning as ClientRequest/InternalRequest
 * in @rundown/types — a client-role response never CONTAINS the fields a
 * client may not see, rather than containing and hiding them. */
export interface BacklogRow {
  readonly id: string;
  readonly number: number;
  readonly title: string;
  readonly type: WorkType;
  readonly priority: Priority;
  readonly approxHours: number;
  readonly ownerName: string | null;
  readonly ownSprint: boolean;
  readonly backlogRank: string;
  /** A client's unanswered suggestion. Pins the item in the projection for both sides. */
  readonly placedSprintId: string | null;
  /**
   * The one grouping a request has — what used to be `area` and `projectName`
   * both. See the `projects` table in apps/api/src/db/schema/work.ts.
   */
  readonly projectName: string | null;
}

export interface InternalBacklogRow extends BacklogRow {
  readonly ownerId: string | null;
  readonly needsReview: boolean;
  readonly isHotfix: boolean;
  readonly visibleToClient: boolean;
  /** Vendor vocabulary, never sent to a client. What the row's status menu checks. */
  readonly boardColumn: BoardColumn;
}

/** `ownerId` exists only on the internal projection, the same tell
 * `isInternalRequest` uses in features/requests/api.ts. */
export function isInternalBacklogRow(row: BacklogRow): row is InternalBacklogRow {
  return 'ownerId' in row;
}

export interface SprintSummary {
  readonly id: string;
  readonly key: string;
  readonly name: string;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly state: SprintState;
  /** Echoed from the server's own `ProjectionSprint` input — see
   * backlog.service.ts's SprintSummary doc comment — so a local recompute
   * (useBacklogRank's optimistic drag preview) can rebuild an identical
   * projection input without a second round trip. */
  readonly committedHours: number;
  readonly unfinishedHours: number;
}

/**
 * `ProjectionResult` (keys, landings, load, loadByOwner…) is imported straight
 * from @rundown/domain rather than redeclared here — it is the exact shape
 * `project()` returns, and the API's projection endpoint is documented (see
 * backlog.service.ts) to return it unmodified plus these extra fields.
 * Redeclaring it by hand would be one more place the two could drift apart.
 *
 * The per-owner three are OPTIONAL, not nullable, and that is the same tell
 * the People list uses for `grade`: a client-role response never CONTAINS
 * them, because "is any one person overbooked" is a staffing question the client is not
 * shown. Typing them optional is what forces every reader to handle their
 * absence instead of rendering `undefined` into a capacity bar.
 */
export interface ProjectionCapacity {
  readonly monthlyHours: number;
  readonly weeklyHours: number;
  readonly hotfixReservePct: number;
  /** What the per-sprint figures (`plannableHours`, `reserveHours`) are scaled by. */
  readonly sprintWeeks: number;
  readonly reserveHours: number;
}

export interface ProjectionResponse extends Omit<ProjectionResult, 'loadByOwner'> {
  readonly sprints: readonly SprintSummary[];
  readonly capacity: ProjectionCapacity;
  readonly loadByOwner?: ProjectionResult['loadByOwner'];
  readonly ownerPlannableHours?: Readonly<Record<string, number>>;
  readonly ownerNames?: Readonly<Record<string, string>>;
}

function backlogPath(workspaceId: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}/backlog`;
}

export function fetchBacklog(workspaceId: string, signal?: AbortSignal): Promise<BacklogRow[]> {
  return api.get<BacklogRow[]>(backlogPath(workspaceId), signal);
}

export interface RankBacklogInput {
  readonly beforeId?: string;
  readonly afterId?: string;
}

export function rankBacklogItem(
  workspaceId: string,
  requestId: string,
  input: RankBacklogInput,
): Promise<BacklogRow> {
  return api.patch<BacklogRow>(`${backlogPath(workspaceId)}/${encodeURIComponent(requestId)}/rank`, input);
}

/** Client suggestion — see backlog.service.ts `place`. Null withdraws it. */
export function placeBacklogItem(
  workspaceId: string,
  requestId: string,
  sprintId: string | null,
): Promise<BacklogRow> {
  return api.put<BacklogRow>(`${backlogPath(workspaceId)}/${encodeURIComponent(requestId)}/placement`, {
    sprintId,
  });
}

export function fetchProjection(
  workspaceId: string,
  capacityMode: CapacityMode,
  fill: FillMode,
  signal?: AbortSignal,
): Promise<ProjectionResponse> {
  const params = new URLSearchParams({ capacityMode, fill });
  return api.get<ProjectionResponse>(`${backlogPath(workspaceId)}/projection?${params}`, signal);
}

/** Every key here starts `['ws', workspaceId]` — see lib/queryKeys.ts's HARD
 * RULE, which this local set still follows even though it is not declared in
 * that shared file. */
export const backlogKeys = {
  list: (workspaceId: string) => ['ws', workspaceId, 'backlog'] as const,
  projection: (workspaceId: string, capacityMode: CapacityMode, fill: FillMode) =>
    ['ws', workspaceId, 'backlog', 'projection', capacityMode, fill] as const,
};

export function backlogQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: backlogKeys.list(workspaceId),
    queryFn: ({ signal }) => fetchBacklog(workspaceId, signal),
  });
}

export function projectionQueryOptions(
  workspaceId: string,
  capacityMode: CapacityMode,
  fill: FillMode = 'strict-rank',
) {
  return queryOptions({
    queryKey: backlogKeys.projection(workspaceId, capacityMode, fill),
    queryFn: ({ signal }) => fetchProjection(workspaceId, capacityMode, fill, signal),
  });
}
